import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Card, Grid, Tabs, Tab, Box, Paper, Alert, Snackbar } from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  ListAlt as ListAltIcon,
  HelpOutline as HelpOutlineIcon,
} from "@mui/icons-material";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import TableTags from "../TableTags";
import FormImportacionTags from "../Form/FormImportacionTags";

const HeaderTags = ({ refreshFlag, onError }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [importSuccess, setImportSuccess] = useState(false);
  const [importError, setImportError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleTabChange = (event, newValue) => {
    if (busy) return;
    setActiveTab(newValue);
    if (newValue !== 0) {
      setImportSuccess(false);
      setImportError(null);
    }
  };

  const handleImportSuccess = useCallback((result) => {
    setImportSuccess(true);
    setImportError(null);

    setSnackbar({
      open: true,
      message: result.message || "Tags importados exitosamente",
      severity: "success",
    });
  }, []);

  const handleImportError = useCallback(
    (error) => {
      setImportError(error);
      setImportSuccess(false);

      setSnackbar({
        open: true,
        message: error || "Error al importar los tags",
        severity: "error",
      });

      if (onError) {
        onError(error);
      }
    },
    [onError]
  );

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const tabsConfig = [
    {
      label: "Importar Tags",
      icon: <CloudUploadIcon sx={{ fontSize: 20, mr: 1 }} />,
      value: 0,
      description: "Importe tags desde archivo Excel",
    },
    {
      label: "Lista de Tags",
      icon: <ListAltIcon sx={{ fontSize: 20, mr: 1 }} />,
      value: 1,
      description: "Administre los tags existentes",
    },
    {
      label: "Instrucciones",
      icon: <HelpOutlineIcon sx={{ fontSize: 20, mr: 1 }} />,
      value: 2,
      description: "Guía paso a paso para importar",
    },
  ];

  // Convertir lotFilter a número si es válido

  return (
    <MDBox position="relative" sx={{ mt: -2, mx: -3, py: 2, px: -2 }}>
      <Card
        sx={{
          mt: 1,
          mx: 3,
          py: 3,
          px: 3,
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        {/* Header con gradiente */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1e293b 0%, #0b4f6c 100%)",
            margin: -3,
            marginBottom: 2,
            padding: 2.5,
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
            <Grid item xs={12} md={6}>
              <MDTypography
                variant="h5"
                color="white"
                fontWeight="bold"
                sx={{ fontSize: { xs: "1.2rem", sm: "1.4rem" }, lineHeight: 1.35 }}
              >
                Gestión de Tags
              </MDTypography>
              <MDTypography
                variant="body2"
                color="white"
                opacity={0.85}
                sx={{ mt: 0.5, fontSize: "0.875rem", lineHeight: 1.6 }}
              >
                Importe y administre los tags del sistema de manera sencilla
              </MDTypography>
            </Grid>
          </Grid>

          {importSuccess && (
            <Alert
              severity="success"
              sx={{
                mt: 2,
                borderRadius: 2,
                bgcolor: "rgba(76, 175, 80, 0.1)",
                color: "#4caf50",
                "& .MuiAlert-icon": {
                  color: "#4caf50",
                },
              }}
            >
              <MDTypography variant="body2" fontWeight="bold">
                ¡Importación completada!
              </MDTypography>
              <MDTypography variant="caption">
                Los tags fueron importados. Puedes revisar el resultado o abrir la lista.
              </MDTypography>
            </Alert>
          )}

          {importError && (
            <Alert
              severity="error"
              sx={{
                mt: 2,
                borderRadius: 2,
                bgcolor: "rgba(244, 67, 54, 0.1)",
                color: "#f44336",
                "& .MuiAlert-icon": {
                  color: "#f44336",
                },
              }}
              onClose={() => setImportError(null)}
            >
              <MDTypography variant="body2" fontWeight="bold">
                Error en la importación
              </MDTypography>
              <MDTypography variant="caption">{importError}</MDTypography>
            </Alert>
          )}
        </Box>

        {importSuccess && !busy && (
          <MDButton color="info" variant="outlined" onClick={() => setActiveTab(1)} sx={{ mb: 2 }}>
            Ver lista de tags
          </MDButton>
        )}
        {/* Tabs personalizados */}
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
                background: "linear-gradient(90deg, #0b4f6c 0%, #1e7e6c 100%)",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                minHeight: 48,
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: "rgba(11, 79, 108, 0.04)",
                  borderRadius: 1,
                },
                "&.Mui-selected": {
                  color: "#0b4f6c",
                },
              },
            }}
          >
            {tabsConfig.map((tab) => (
              <Tab
                disabled={busy}
                key={tab.value}
                icon={tab.icon}
                label={tab.label}
                iconPosition="start"
                sx={{
                  "& .MuiTab-iconWrapper": {
                    transition: "transform 0.2s",
                  },
                  "&:hover .MuiTab-iconWrapper": {
                    transform: "scale(1.05)",
                  },
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Contenido de los panels */}
        <Box sx={{ mt: 2 }}>
          {activeTab === 0 && (
            <Box
              sx={{
                animation: "fadeIn 0.3s ease-in-out",
                "@keyframes fadeIn": {
                  "0%": { opacity: 0, transform: "translateY(10px)" },
                  "100%": { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              <FormImportacionTags
                onBusyChange={setBusy}
                onImportSuccess={handleImportSuccess}
                onError={handleImportError}
              />
            </Box>
          )}

          {activeTab === 1 && (
            <Box
              sx={{
                animation: "fadeIn 0.3s ease-in-out",
                "@keyframes fadeIn": {
                  "0%": { opacity: 0, transform: "translateY(10px)" },
                  "100%": { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              <TableTags refreshFlag={refreshFlag} onError={onError} />
            </Box>
          )}

          {activeTab === 2 && (
            <Box
              sx={{
                animation: "fadeIn 0.3s ease-in-out",
                "@keyframes fadeIn": {
                  "0%": { opacity: 0, transform: "translateY(10px)" },
                  "100%": { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                }}
              >
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background: "linear-gradient(135deg, #0b4f6c 0%, #1e7e6c 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <HelpOutlineIcon sx={{ color: "white", fontSize: 28 }} />
                  </Box>
                  <Box>
                    <MDTypography variant="h5" fontWeight="bold" color="#1e293b">
                      Guía de Importación
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary">
                      Siga estos pasos para importar tags correctamente
                    </MDTypography>
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <MDTypography variant="subtitle1" fontWeight="bold" color="#0b4f6c" gutterBottom>
                    📋 Pasos a seguir:
                  </MDTypography>
                  <Box component="ol" sx={{ margin: 0, paddingLeft: "1.5rem", mt: 1 }}>
                    {[
                      "Prepare un archivo Excel (.xlsx) con las siguientes columnas:",
                      "Haga clic en el área de selección para elegir el archivo",
                      "Verifique que el archivo seleccionado sea correcto",
                      "Presione el botón 'Procesar Tags'",
                      "Espere la confirmación de importación",
                    ].map((step, index) => (
                      <Box
                        key={index}
                        component="li"
                        sx={{
                          mb: 1.5,
                          color: "#334155",
                          "&::marker": {
                            color: "#0b4f6c",
                            fontWeight: "bold",
                          },
                        }}
                      >
                        <MDTypography variant="body2">
                          {step}
                          {index === 0 && (
                            <Box
                              component="ul"
                              sx={{ margin: 0.5, paddingLeft: "1.5rem", mt: 0.5 }}
                            >
                              <li>
                                <strong>Reverse Number</strong> - Número reverso (requerido)
                              </li>
                              <li>
                                <strong>Number Below the QR</strong> - Número debajo del QR
                                (requerido)
                              </li>
                              <li>
                                <strong>Code QR</strong> - Código QR único (requerido)
                              </li>
                              <li>
                                <strong>ESTADO</strong> - Estado del tag (requerido)
                              </li>
                              <li>
                                <strong>LOTE</strong> - Número de lote (requerido)
                              </li>
                              <li>
                                <strong>ESTADO TAGS</strong> - Estado adicional (requerido)
                              </li>
                            </Box>
                          )}
                        </MDTypography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box
                  sx={{
                    bgcolor: "#fff8e1",
                    borderRadius: 2,
                    p: 2,
                    border: "1px solid #ffe082",
                    mb: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Box sx={{ fontSize: 20 }}>💡</Box>
                    <MDTypography variant="subtitle2" fontWeight="bold" color="#ed6c02">
                      Notas importantes:
                    </MDTypography>
                  </Box>
                  <MDTypography variant="body2" color="text.secondary">
                    • Todas las columnas son <strong>OBLIGATORIAS</strong>
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    • El archivo será validado automáticamente al seleccionarlo
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    • No se permiten valores duplicados en los campos únicos
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    • El archivo no debe exceder los 40MB de tamaño
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    • Los errores se mostrarán detalladamente con el número de fila
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    • Puede filtrar por lote específico usando el campo de filtro
                  </MDTypography>
                </Box>

                <Box
                  sx={{
                    bgcolor: "#ffebee",
                    borderRadius: 2,
                    p: 2,
                    border: "1px solid #ef9a9a",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Box sx={{ fontSize: 20 }}>🔧</Box>
                    <MDTypography variant="subtitle2" fontWeight="bold" color="#c62828">
                      Solución de problemas comunes:
                    </MDTypography>
                  </Box>
                  <MDTypography variant="caption" color="text.secondary" component="div">
                    <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                      <li>
                        <strong>Error de columnas:</strong> Asegúrese que los nombres de las
                        columnas sean exactamente los requeridos
                      </li>
                      <li>
                        <strong>Duplicados en archivo:</strong> Revise que no haya valores repetidos
                        en las columnas únicas
                      </li>
                      <li>
                        <strong>Duplicados en BD:</strong> Los códigos QR ya existen en el sistema
                      </li>
                      <li>
                        <strong>Campos vacíos:</strong> Todos los campos son obligatorios
                      </li>
                      <li>
                        <strong>Error de conexión:</strong> Verifique su conexión a internet
                      </li>
                    </ul>
                  </MDTypography>
                </Box>

                <Box
                  sx={{
                    mt: 3,
                    pt: 2,
                    borderTop: "1px solid #e2e8f0",
                    textAlign: "center",
                  }}
                >
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    Sistema de Gestión de Tags v2.0 (Con paginación) | Para soporte contacte al
                    administrador
                  </MDTypography>
                </Box>
              </Paper>
            </Box>
          )}
        </Box>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            borderRadius: 2,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          <MDTypography variant="body2" fontWeight="bold">
            {snackbar.severity === "success" ? "¡Éxito!" : "Error"}
          </MDTypography>
          <MDTypography variant="caption">{snackbar.message}</MDTypography>
        </Alert>
      </Snackbar>
    </MDBox>
  );
};

HeaderTags.defaultProps = {
  refreshFlag: false,
  onError: null,
};

HeaderTags.propTypes = {
  refreshFlag: PropTypes.bool,
  onError: PropTypes.func,
};

export default HeaderTags;
